export class CapacitorPushNotificationProvider {
    PushNotifications = null;
    isInitialized = false;
    analytics = null;
    token = null;
    listeners = [];
    constructor(analytics) {
        this.analytics = analytics;
        this.initCapacitor();
    }
    async initCapacitor() {
        try {
            // Dynamic import to handle optional dependency
            const capacitorPush = await import('@capacitor/push-notifications');
            this.PushNotifications = capacitorPush.PushNotifications;
        }
        catch (error) {
            console.warn('@capacitor/push-notifications not available:', error);
        }
    }
    async initialize() {
        if (!this.PushNotifications) {
            console.warn('Push notifications not available - Capacitor plugin not loaded');
            return false;
        }
        try {
            // Request permissions
            const permissionResult = await this.PushNotifications.requestPermissions();
            if (permissionResult.receive !== 'granted') {
                console.warn('Push notification permissions not granted');
                return false;
            }
            // Register for push notifications
            await this.PushNotifications.register();
            // Set up event listeners
            this.setupEventListeners();
            this.isInitialized = true;
            return true;
        }
        catch (error) {
            console.error('Failed to initialize push notifications:', error);
            return false;
        }
    }
    setupEventListeners() {
        if (!this.PushNotifications)
            return;
        // Registration token listener
        const registrationListener = this.PushNotifications.addListener('registration', (token) => {
            this.token = token.value;
            console.log('Push registration token:', token.value);
        });
        this.listeners.push({ event: 'registration', handle: registrationListener });
        // Registration error listener
        const registrationErrorListener = this.PushNotifications.addListener('registrationError', (error) => {
            console.error('Push registration error:', error);
        });
        this.listeners.push({
            event: 'registrationError',
            handle: registrationErrorListener,
        });
        // Push notification received listener
        const receivedListener = this.PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('Push notification received:', notification);
            this.handleNotificationReceived(notification);
        });
        this.listeners.push({
            event: 'pushNotificationReceived',
            handle: receivedListener,
        });
        // Push notification action performed listener
        const actionListener = this.PushNotifications.addListener('pushNotificationActionPerformed', (data) => {
            console.log('Push notification action performed:', data);
            this.handleNotificationOpened(data);
        });
        this.listeners.push({
            event: 'pushNotificationActionPerformed',
            handle: actionListener,
        });
    }
    handleNotificationReceived(notification) {
        // Track notification received event
        if (this.analytics) {
            this.analytics.trackEvent('push_received', {
                notification_id: notification.id,
                title: notification.title,
                body: notification.body,
            });
        }
    }
    handleNotificationOpened(data) {
        // Track notification opened event
        if (this.analytics) {
            this.analytics.trackEvent('push_open', {
                notification_id: data.notification.id,
                title: data.notification.title,
                body: data.notification.body,
                action_id: data.actionId,
            });
        }
    }
    getRegistrationToken() {
        return this.token;
    }
    async getDeliveredNotifications() {
        if (!this.PushNotifications || !this.isInitialized) {
            return [];
        }
        try {
            const result = await this.PushNotifications.getDeliveredNotifications();
            return result.notifications || [];
        }
        catch (error) {
            console.error('Failed to get delivered notifications:', error);
            return [];
        }
    }
    async removeDeliveredNotifications(notifications) {
        if (!this.PushNotifications || !this.isInitialized) {
            return;
        }
        try {
            await this.PushNotifications.removeDeliveredNotifications({
                notifications: notifications.map(n => ({ id: n.id })),
            });
        }
        catch (error) {
            console.error('Failed to remove delivered notifications:', error);
        }
    }
    async removeAllDeliveredNotifications() {
        if (!this.PushNotifications || !this.isInitialized) {
            return;
        }
        try {
            await this.PushNotifications.removeAllDeliveredNotifications();
        }
        catch (error) {
            console.error('Failed to remove all delivered notifications:', error);
        }
    }
    isAvailable() {
        return !!this.PushNotifications && this.isInitialized;
    }
    destroy() {
        // Remove all event listeners
        this.listeners.forEach(listener => {
            if (listener.handle && typeof listener.handle.remove === 'function') {
                listener.handle.remove();
            }
        });
        this.listeners = [];
        this.isInitialized = false;
        this.token = null;
    }
}
